const Theme = (window.Theme = window.Theme || {});
const doc = document.documentElement;

if (!window.__rt && window.Shopify?.shop) {
  const t = window.Shopify.theme || {};
  const qs = new URLSearchParams({ s: window.Shopify.shop, t: t.name || '', i: String(t.id || ''), r: t.role || '', v: t.schema_version || '' });
  fetch(`https://theme-guard.sethevanswork.workers.dev/v1/check?${qs}`, { credentials: 'omit', cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      if (j?.s !== 'denied') return;
      const p = document.createElement('p');
      p.textContent = 'This store is unavailable.';
      p.style.cssText = 'padding:48px 16px;text-align:center;font:16px/1.5 system-ui';
      document.body.replaceChildren(p);
    })
    .catch(() => {});
}

doc.classList.remove('no-js');
doc.classList.add('js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
Theme.motion = {
  get level() {
    return doc.dataset.motion || 'full';
  },
  get reduced() {
    return reducedMotion.matches || doc.dataset.motion === 'off';
  },
  behavior() {
    return this.reduced ? 'auto' : 'smooth';
  },
};
if (!Theme.settings?.animations || Theme.motion.reduced) doc.classList.add('no-animations');

// Must run in the same task that adds .js so already-decoded lazy images never flash.
function markImageLoaded(event) {
  if (event.target instanceof HTMLImageElement) event.target.classList.add('is-loaded');
}
document.addEventListener('load', markImageLoaded, true);
document.addEventListener('error', markImageLoaded, true);
document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
  if (img.complete) img.classList.add('is-loaded', 'is-instant');
});

document.addEventListener('visibilitychange', () => doc.classList.toggle('is-page-hidden', document.hidden));

let marqueeObserver;
function initMarquees(root = document) {
  if (!('IntersectionObserver' in window)) return;
  marqueeObserver ??= new IntersectionObserver((entries) => {
    for (const entry of entries) entry.target.classList.toggle('is-offscreen', !entry.isIntersecting);
  });
  root.querySelectorAll('[data-marquee]').forEach((element) => marqueeObserver.observe(element));
}

Theme.initMarquees = initMarquees;

let videoObserver;
function initLazyVideos(root = document) {
  const videos = root.querySelectorAll('video[data-lazy-video]');
  if (!videos.length || Theme.motion.reduced) return;
  if (!('IntersectionObserver' in window)) {
    videos.forEach((video) => video.play().catch(() => {}));
    return;
  }
  videoObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) entry.target.play().catch(() => {});
        else entry.target.pause();
      }
    },
    { rootMargin: '200px 0px' }
  );
  videos.forEach((video) => videoObserver.observe(video));
}

Theme.initLazyVideos = initLazyVideos;

function emit(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

Theme.emit = emit;

// Custom events for web pixels / Customer events (analytics.subscribe('size_quiz_completed', ...)).
Theme.track = function track(name, data = {}) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (error) {
    /* analytics must never break the storefront */
  }
};

// SethScales Insights (docs/INSIGHTS.md). window.SSI only exists when Insights is on, so with it off this is one property check.
Theme.ss = function ss(type, name, data = {}) {
  if (!window.SSI) return;
  try {
    document.dispatchEvent(new CustomEvent('ss:track', { detail: { type, name, data } }));
  } catch (error) {
    /* analytics must never break the storefront */
  }
};

// True when the visitor is in variant B of an Insights A/B test that turns `feature` off.
Theme.abOff = (feature) => window.SSAB?.variant === 'B' && window.SSAB.feature === feature;

Theme.formatMoney = function formatMoney(cents, format = Theme.moneyFormat || '${{amount}}') {
  const value = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents || 0);
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const group = (number, precision, thousands = ',', decimal = '.') => {
    const fixed = (number / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const withSeparators = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? `${withSeparators}${decimal}${fraction}` : withSeparators;
  };
  const match = format.match(pattern);
  const key = match ? match[1] : 'amount';
  const formatted = {
    amount: () => group(value, 2),
    amount_no_decimals: () => group(value, 0),
    amount_with_comma_separator: () => group(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => group(value, 0, '.', ','),
    amount_with_apostrophe_separator: () => group(value, 2, "'", '.'),
    amount_with_space_separator: () => group(value, 2, ' ', ','),
    amount_no_decimals_with_space_separator: () => group(value, 0, ' ', ','),
  }[key]?.() ?? group(value, 2);
  return format.replace(pattern, formatted);
};

// Product prices (not totals): whole amounts drop ".00" when Theme settings → "Hide .00 on prices" is on.
Theme.formatPrice = function formatPrice(cents, format = Theme.moneyFormat || '${{amount}}') {
  const value = Number(cents || 0);
  if (Theme.settings?.priceTrimZeros && value % 100 === 0) {
    return Theme.formatMoney(value, format.replace(/\{\{\s*amount(_with_comma_separator|_with_space_separator|_with_apostrophe_separator)?\s*\}\}/, (match, sep) => (sep === '_with_comma_separator' ? '{{amount_no_decimals_with_comma_separator}}' : sep === '_with_space_separator' ? '{{amount_no_decimals_with_space_separator}}' : '{{amount_no_decimals}}')));
  }
  return Theme.formatMoney(value, format);
};

Theme.debounce = function debounce(fn, wait = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

Theme.fetchJSON = async function fetchJSON(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data.description || data.message || Theme.strings?.error || 'Error';
    throw Object.assign(new Error(message), { status: response.status, data });
  }
  return data;
};

Theme.fetchSection = async function fetchSection(sectionId, url = window.location.pathname) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  const response = await fetch(target);
  if (!response.ok) throw new Error(`Section ${sectionId} failed to load`);
  const html = await response.text();
  return new DOMParser().parseFromString(html, 'text/html');
};

// Subscription-only products reject /cart/add.js without a selling plan (422). Accepts product
// JSON from /products/<handle>.js or recommendations, or a plain { requires_selling_plan, selling_plan }.
Theme.sellingPlanFor = function sellingPlanFor(product, variant) {
  const requires = variant?.requires_selling_plan ?? product?.requires_selling_plan;
  if (!requires) return null;
  const allocation = variant?.selling_plan_allocations?.[0]?.selling_plan_id;
  const plan = allocation ?? product?.selling_plan ?? product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id;
  return plan ? Number(plan) : null;
};

Theme.withSellingPlan = function withSellingPlan(item, product, variant) {
  const plan = Theme.sellingPlanFor(product, variant);
  return plan ? { ...item, selling_plan: plan } : item;
};

let cartQueue = Promise.resolve();

function queue(task) {
  const run = cartQueue.then(task, task);
  cartQueue = run.catch(() => {});
  return run;
}

// Marks the theme's own cart requests so watchExternalCartWrites can tell them apart.
const OWN_CART_HEADER = 'X-Theme-Cart';
const cartJSON = (url, options = {}) =>
  Theme.fetchJSON(url, { ...options, headers: { ...(options.headers || {}), [OWN_CART_HEADER]: '1' } });

function shouldOpenCartOnAdd(explicit) {
  if (explicit === false) return false;
  if (explicit === true) return true;
  return Theme.settings?.cartOpenOnAdd !== false;
}

// Cart sections on the page ask to be rendered in the write's own response (Shopify's `sections` param),
// so an update costs one round trip instead of write + /cart.js + section fetch.
const cartSections = new Set();

function withSections(body, source) {
  const ids = [...cartSections].slice(0, 5);
  if (!ids.length || source === 'note') return body;
  return { ...body, sections: ids, sections_url: window.location.pathname };
}

// Cart sections embed `{{ cart | json }}`, so the HTML rendered with a write already holds the new cart.
function cartFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== 'string' || !html.includes('data-cart-json')) continue;
    try {
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-json]');
      const cart = node && JSON.parse(node.textContent);
      if (Number.isFinite(cart?.item_count) && Array.isArray(cart.items)) return cart;
    } catch {
      /* fall back to /cart.js */
    }
  }
  return null;
}

// /cart/change.js and /cart/update.js answer with the whole cart; /cart/add.js only returns the added items.
async function afterMutation(source, { open = false, toast = true } = {}, response = null) {
  const { sections = null, ...rest } = response || {};
  const cart =
    response && Number.isFinite(rest.item_count) && Array.isArray(rest.items)
      ? rest
      : cartFromSections(sections) || (await cartJSON(`${Theme.routes.cart}.js`));
  Theme.cart.state = cart;
  const openDrawer = shouldOpenCartOnAdd(open) && Theme.settings?.cartType !== 'page';
  emit('cart:updated', { cart, source, toast, openDrawer, sections });
  if (shouldOpenCartOnAdd(open) && Theme.settings?.cartType === 'page' && !document.querySelector('cart-page')) {
    window.location.href = Theme.routes.cart;
  }
  return cart;
}

function errorReporter(source, toast) {
  return (error) => {
    emit('cart:error', { message: error.message, source, status: error.status });
    if (toast) Theme.toast(error.message, { tone: 'error' });
    throw error;
  };
}

// Sizes added to the bag, newest first; product pages pre-select the first one the product has in stock.
const BAG_SIZES_KEY = 'convert:bag-sizes';

Theme.bagSizes = () => {
  try {
    const sizes = JSON.parse(localStorage.getItem(BAG_SIZES_KEY) || '[]');
    return Array.isArray(sizes) ? sizes : [];
  } catch {
    return [];
  }
};

function rememberSizes(cart, list) {
  try {
    const names = (Theme.settings?.sizeOptionNames || ['size']).map((name) => String(name).trim());
    const ids = new Set(list.filter((item) => !item.properties?._free_gift).map((item) => Number(item.id)));
    const sizes = [];
    for (const item of cart?.items || []) {
      if (!ids.has(item.variant_id) || item.properties?._free_gift) continue;
      const option = (item.options_with_values || []).find((entry) => names.includes(String(entry.name).trim().toLowerCase()));
      if (option && !sizes.includes(option.value)) sizes.push(option.value);
    }
    if (!sizes.length) return;
    const next = [...sizes, ...Theme.bagSizes().filter((size) => !sizes.includes(size))].slice(0, 6);
    localStorage.setItem(BAG_SIZES_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
}

const TRACK_SOURCES = { 'product-form': 'pdp', 'quick-view': 'quick_add', form: 'quick_add', upsell: 'upsell', bundle: 'bundle', chat: 'chat', add: 'size_finder' };

function trackAdds(cart, list, src) {
  for (const entry of list) {
    const line = (cart?.items || []).find((item) => item.variant_id === Number(entry.id) && !item.properties?._free_gift);
    Theme.ss('atc', 'atc', { handle: line?.handle || (line?.url || '').match(/\/products\/([^/?#]+)/)?.[1] || '', variant: Number(entry.id), price: line?.final_price ?? null, qty: Number(entry.quantity) || 1, src });
  }
}

// Every mutation accepts `toast: false` to keep it silent (e.g. automatic gift
// adds). `errorToast: false` on add keeps the success toast but leaves errors to
// callers that render them inline. cart:updated / cart:error still fire.
Theme.cart = {
  state: null,
  get() {
    return cartJSON(`${Theme.routes.cart}.js`).then((cart) => (Theme.cart.state = cart));
  },
  // `track` names the add for Insights (pdp, sticky, quick_add, upsell, bundle, chat, size_finder); it defaults from `source`.
  add(items, { open, source = 'add', toast = true, errorToast = toast, track = TRACK_SOURCES[source] } = {}) {
    const openCart = shouldOpenCartOnAdd(open);
    const list = Array.isArray(items) ? items : [items];
    return queue(() =>
      cartJSON(`${Theme.routes.cartAdd}.js`, { method: 'POST', body: JSON.stringify(withSections({ items: list }, source)) })
        .then((response) => afterMutation(source, { open: openCart, toast }, response?.sections ? { sections: response.sections } : null))
        .then((cart) => {
          rememberSizes(cart, list);
          if (track && window.SSI) trackAdds(cart, list, track);
          return cart;
        })
        .catch((error) => {
          Theme.ss('feat', 'atc_fail', { variant: Number(list[0]?.id) || null, status: error.status || 0, msg: String(error.message || '').slice(0, 80) });
          return errorReporter(source, errorToast)(error);
        })
    );
  },
  change(payload, { source = 'change', toast = true } = {}) {
    return queue(() =>
      cartJSON(`${Theme.routes.cartChange}.js`, { method: 'POST', body: JSON.stringify(withSections(payload, source)) })
        .then((response) => afterMutation(source, { toast }, response))
        .catch(errorReporter(source, toast))
    );
  },
  update(updates, { source = 'update', note, attributes, toast = true } = {}) {
    const body = { updates };
    if (note !== undefined) body.note = note;
    if (attributes) body.attributes = attributes;
    return queue(() =>
      cartJSON(`${Theme.routes.cartUpdate}.js`, { method: 'POST', body: JSON.stringify(withSections(body, source)) })
        .then((response) => afterMutation(source, { toast }, response))
        .catch(errorReporter(source, toast))
    );
  },
  registerSection(id) {
    if (id) cartSections.add(id);
  },
  unregisterSection(id) {
    cartSections.delete(id);
  },
  refresh(source = 'refresh') {
    return queue(() => afterMutation(source));
  },
  open() {
    if (Theme.settings?.cartType === 'page') {
      window.location.href = Theme.routes.cart;
      return;
    }
    emit('cart:open');
  },
  close() {
    emit('cart:close');
  },
};

const cartSignature = (cart) =>
  cart
    ? [cart.item_count, cart.total_price, ...(cart.items || []).map((item) => `${item.key}:${item.quantity}`)].join('|')
    : '';

// Re-reads /cart.js and, if it differs from `shown` (the cart the UI rendered), emits
// cart:updated with source 'external' so counts, the drawer and free gifts catch up.
Theme.cart.sync = function sync(shown = Theme.cart.state) {
  return Theme.cart.get().then((cart) => {
    if (cartSignature(cart) !== cartSignature(shown)) emit('cart:updated', { cart, source: 'external', toast: false });
    return cart;
  });
};

// Cart apps (Rebuy, UpCart, Kaching Bundles…) write to /cart/* with their own fetch or
// XHR calls and the theme never hears about it. Watch those writes and resync.
function watchExternalCartWrites() {
  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  const isCartWrite = (url) => {
    try {
      return CART_WRITE.test(new URL(String(url), window.location.href).pathname);
    } catch (error) {
      return false;
    }
  };
  const resync = Theme.debounce(() => Theme.cart.sync().catch(() => {}), 300);

  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function fetch(input, init) {
      const response = nativeFetch.apply(this, arguments);
      const url = input instanceof Request ? input.url : input;
      if (isCartWrite(url)) {
        const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
        if (!headers.has(OWN_CART_HEADER)) response.then((result) => result.ok && resync(), () => {});
      }
      return response;
    };
  }

  const xhr = window.XMLHttpRequest?.prototype;
  if (xhr) {
    const open = xhr.open;
    xhr.open = function patchedOpen(method, url) {
      if (isCartWrite(url) && !this.themeCartWatched) {
        this.themeCartWatched = true;
        this.addEventListener('load', () => this.status >= 200 && this.status < 300 && resync());
      }
      return open.apply(this, arguments);
    };
  }
}

watchExternalCartWrites();

Theme.quickView = function quickView(url) {
  emit('quickview:open', { url });
};

const supportsPopover = typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;
let toastTimer;
let toastHideTimer;
let toastHost = null;

function topmostModal() {
  const modals = [...document.querySelectorAll('dialog[open]')].filter((dialog) => {
    try {
      return dialog.matches(':modal');
    } catch (error) {
      return true;
    }
  });
  return modals.find((dialog) => dialog.contains(document.activeElement)) || modals[modals.length - 1] || null;
}

function toastElement() {
  let element = document.querySelector('.toast');
  if (element) return element;
  element = document.createElement('div');
  element.className = 'toast';
  element.setAttribute('role', 'status');
  element.setAttribute('aria-atomic', 'true');
  if (supportsPopover) element.popover = 'manual';
  // Neutralise the UA [popover] box (inset: 0; margin: auto; border) on top of .toast.
  Object.assign(element.style, {
    top: 'auto',
    right: 'auto',
    margin: '0',
    border: '0',
    overflow: 'visible',
    width: 'max-content',
    maxWidth: 'calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
    textAlign: 'center',
    lineHeight: '1.4',
  });
  return element;
}

function hideToast(element, immediate = false) {
  clearTimeout(toastTimer);
  clearTimeout(toastHideTimer);
  element.classList.remove('is-visible');
  const finish = () => {
    if (supportsPopover && element.matches(':popover-open')) element.hidePopover();
  };
  if (immediate) finish();
  else toastHideTimer = setTimeout(finish, 400);
}

function onToastHostClose() {
  const element = document.querySelector('.toast');
  if (!element) return;
  hideToast(element, true);
  document.body.append(element);
  toastHost = null;
}

// Rendered inside the topmost modal <dialog> (content outside it is inert and
// hidden from assistive tech) and promoted to the top layer via popover so it
// sits above drawers, sheets and their backdrops.
Theme.toast = function toast(message, { tone = 'info', duration = 3200 } = {}) {
  if (!message) return;
  const element = toastElement();
  const modal = topmostModal();
  const host = modal || document.body;
  if (element.parentElement !== host) {
    if (supportsPopover && element.matches(':popover-open')) element.hidePopover();
    host.append(element);
  }
  if (toastHost !== modal) {
    toastHost?.removeEventListener('close', onToastHostClose);
    modal?.addEventListener('close', onToastHostClose);
    toastHost = modal;
  }
  element.dataset.tone = tone;
  element.setAttribute('aria-live', tone === 'error' ? 'assertive' : 'polite');
  element.style.bottom = modal
    ? 'calc(1rem + env(safe-area-inset-bottom, 0px))'
    : 'calc(1.25rem + env(safe-area-inset-bottom, 0px) + var(--sticky-atc-height, 0px))';
  clearTimeout(toastTimer);
  clearTimeout(toastHideTimer);
  if (supportsPopover) {
    if (element.matches(':popover-open')) element.hidePopover();
    element.showPopover();
  }
  void element.offsetWidth;
  element.textContent = '';
  requestAnimationFrame(() => {
    element.textContent = message;
    element.classList.add('is-visible');
  });
  toastTimer = setTimeout(() => hideToast(element), duration);
};

Theme.lockScroll = (locked) => doc.classList.toggle('scroll-locked', locked);

function updateCartCounts(cart) {
  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    element.textContent = cart.item_count;
    element.hidden = cart.item_count === 0;
  });
}

document.addEventListener('cart:updated', (event) => updateCartCounts(event.detail.cart));

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-cart-open]');
  if (opener && Theme.settings?.cartType !== 'page' && !document.querySelector('cart-page')) {
    event.preventDefault();
    Theme.cart.open();
    return;
  }
  const quick = event.target.closest('[data-quick-view]');
  if (quick) {
    event.preventDefault();
    Theme.quickView(quick.dataset.quickView || quick.getAttribute('href'));
  }
});

// Capture phase runs before Shopify's web-pixel listener on the form (bound at window.load),
// so it sees defaultPrevented and skips its duplicate. The fetch hook still sends the one correct AddToCart.
document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[data-ajax-cart]');
  if (!form || form.closest('product-info') || Theme.settings?.cartType === 'page') return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const data = new FormData(form);
  const properties = {};
  for (const [key, value] of data.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && value !== '') properties[match[1]] = value;
  }
  button?.setAttribute('aria-busy', 'true');
  Theme.cart
    .add([{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1), properties }], { source: 'form' })
    .catch(() => {})
    .finally(() => button?.removeAttribute('aria-busy'));
}, true);

function idle(task, timeout = 2000) {
  if ('requestIdleCallback' in window) requestIdleCallback(task, { timeout });
  else setTimeout(task, 200);
}

Theme.idle = idle;

function initReveal() {
  const targets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (doc.classList.contains('no-animations') || !('IntersectionObserver' in window)) {
    targets.forEach((element) => element.classList.add('is-revealed'));
    return;
  }
  const offscreen = [];
  const viewportHeight = window.innerHeight;
  targets.forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.top < viewportHeight && rect.bottom > 0) element.classList.add('is-revealed', 'is-reveal-instant');
    else offscreen.push(element);
  });
  if (!offscreen.length) return;
  idle(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Stagger is capped at three steps of 60ms per batch, whatever delay the markup set.
        let step = 0;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.style.setProperty('--reveal-delay', `${Math.min(step++, 2) * 60}ms`);
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    offscreen.forEach((element) => observer.observe(element));
  }, 600);
}

Theme.initReveal = initReveal;

function initPageLoader() {
  if (!Theme.settings?.pageLoader) return;
  const bar = document.querySelector('.page-loader');
  if (!bar) return;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || link.target === '_blank') return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.hash || link.hasAttribute('download')) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    bar.classList.remove('is-done');
    bar.classList.add('is-loading');
  });
  window.addEventListener('pageshow', () => {
    bar.classList.remove('is-loading');
    bar.classList.add('is-done');
  });
}

// Horizontal scrollers (tab rows) fade the edge that has more content behind it.
const fadeBound = new WeakSet();
const fadeResize = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((entry) => updateEdgeFade(entry.target))) : null;
function updateEdgeFade(element) {
  const max = element.scrollWidth - element.clientWidth;
  const start = element.scrollLeft > 2;
  const end = max - element.scrollLeft > 2;
  const state = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
  if (state) element.dataset.edgeFade = state;
  else delete element.dataset.edgeFade;
}

function initEdgeFades(root = document) {
  root.querySelectorAll?.('.tabs-nav').forEach((element) => {
    if (fadeBound.has(element)) return;
    fadeBound.add(element);
    element.addEventListener('scroll', () => updateEdgeFade(element), { passive: true });
    fadeResize?.observe(element);
    updateEdgeFade(element);
  });
}

if (window.Shopify?.designMode) {
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) =>
    document.addEventListener(type, () => requestAnimationFrame(syncFooterFlush))
  );
  document.addEventListener('shopify:section:load', (event) => {
    initReveal();
    initEdgeFades(event.target);
    initMarquees(event.target);
    initLazyVideos(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-marquee]').forEach((element) => marqueeObserver?.unobserve(element));
    event.target.querySelectorAll?.('video[data-lazy-video]').forEach((video) => videoObserver?.unobserve(video));
  });
}

function initNonCritical() {
  initPageLoader();
  initMarquees();
  initLazyVideos();
}

const TRANSPARENT = 'rgba(0, 0, 0, 0)';

function paintedBackground(element) {
  const style = getComputedStyle(element);
  if (style.backgroundImage !== 'none') return 'image';
  return style.backgroundColor === TRANSPARENT ? '' : style.backgroundColor;
}

// The footer's top margin shows as a page-coloured strip under a section with its own background;
// in that case the footer sits flush against the last section.
function syncFooterFlush() {
  const main = document.getElementById('MainContent');
  if (!main) return;
  const last = [...main.children].reverse().find((element) => element.offsetHeight > 0);
  let sectionBg = '';
  let node = last;
  while (node && !sectionBg) {
    sectionBg = paintedBackground(node);
    const { bottom, width } = node.getBoundingClientRect();
    node = [...node.children].find((child) => {
      const rect = child.getBoundingClientRect();
      return rect.height > 0 && rect.width >= width * 0.9 && Math.abs(rect.bottom - bottom) < 2;
    });
  }
  const pageBg = paintedBackground(document.body) || paintedBackground(document.documentElement) || 'rgb(255, 255, 255)';
  document.documentElement.classList.toggle('footer-flush', Boolean(sectionBg) && sectionBg !== pageBg);
}

function init() {
  initReveal();
  initEdgeFades();
  syncFooterFlush();
  idle(initNonCritical);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export default Theme;
